import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Loader2, FileText, CheckCircle2, Download } from 'lucide-react';
import { toast } from 'sonner';
import {
  createAdhesion,
  fetchClientIp,
  type CreateAdhesionPayload,
  type TokenDealTermsAditivo,
} from '@/services/adhesionService';
import {
  renderTermoPreview,
  renderTermoAditivoPreview,
  formatCentavosBR,
  formatDateBR,
  formatDataAceiteExtenso,
  formatDataHoraBR,
  deriveNomePlano,
  TERMO_ADITIVO_VERSION,
  type RenderTermoData,
  type RenderTermoAditivoData,
} from '@/services/termoRenderer';
import { TermoRendered } from '@/components/TermoRendered';

// =============================================================================
// Etapa 4 — Revisão + Aceite do Termo de Adesão e Uso
// =============================================================================
// Aparece quando o token carrega `deal_terms` com campos da proposta Dgenny
// (num_assistentes, volumes, data_inicio_prevista). Substitui o submit antigo
// (Asaas + Conexa) — agora o servidor cria a Adhesion e o Radar do cs-dgenny
// é atualizado direto. Após sucesso, redireciona pra /cadastro-sucesso.
//
// Modo ADITIVO (`dealTerms.tipo_adesao === 'aditivo'`): mesmo fluxo, mas o
// documento é o Termo de Recurso Adicional (renderTermoAditivoPreview) e o
// resumo mostra o recurso em vez do plano. O payload enviado ao backend é o
// mesmo — o servidor decide pelo token qual termo assinar.

/** Contato adicional vindo da Etapa 3 (v1.5.1). */
export interface ContatoAdicionalProp {
  nome: string;
  cargo?: string | null;
  email: string;
  celular: string;
}

function toContatoTermo(c: ContatoAdicionalProp) {
  return { nome: c.nome, cargo: c.cargo || undefined, email: c.email, telefone: c.celular };
}

function toContatoPayload(c: ContatoAdicionalProp) {
  return { nome: c.nome, cargo: c.cargo || undefined, email: c.email, telefone: c.celular };
}

export interface RevisaoEAceiteEtapaProps {
  token: string;
  dealTerms: Record<string, any> & TokenDealTermsAditivo;
  /** Link de recurso adicional (aditivo). Se omitido, deriva de dealTerms.tipo_adesao. */
  modoAditivo?: boolean;
  empresaHint: { cnpj?: string; razao_social?: string; nome_fantasia?: string } | null;
  customerData: {
    name: string;
    email: string;
    cpfCnpj: string;
    phone: string;
    postalCode: string;
    address: string;
    addressNumber: string;
    complement: string;
    province: string;
    city: string;
  };
  uf: string; // sigla do estado, vinda do CNPJ.ws lookup (estabelecimento.estado.sigla)
  responsavelOperacional: {
    nome: string;
    cpf?: string | null;
    email: string;
    celular: string;
    cargo?: string | null;
  };
  responsavelFinanceiro: {
    nome: string;
    cpf?: string | null;
    email: string;
    celular: string;
    cargo?: string | null;
  };
  /** v1.5.1 — contatos adicionais (já validados na Etapa 3). */
  operacionaisAdicionais?: ContatoAdicionalProp[];
  financeirosAdicionais?: ContatoAdicionalProp[];
  onBack: () => void;
  /**
   * Chamado após aceite criar Adhesion no Radar. O `companyId` retornado pelo
   * backend é propagado pro `/cadastro-sucesso`, onde o CTA opcional de
   * contrato formal vive (em `/contrato-formal/:adhesionId`).
   */
  onAccepted: (adhesionId: string, extras: { companyId: string }) => void;
}

type SignatarioFonte = 'operacional' | 'financeiro' | 'manual';

export function RevisaoEAceiteEtapa({
  token,
  dealTerms,
  modoAditivo,
  empresaHint,
  customerData,
  responsavelOperacional,
  responsavelFinanceiro,
  operacionaisAdicionais,
  financeirosAdicionais,
  uf,
  onBack,
  onAccepted,
}: RevisaoEAceiteEtapaProps) {
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [clientIp, setClientIp] = useState<string>('');
  const [now] = useState(() => new Date());
  const isAditivo = modoAditivo ?? dealTerms.tipo_adesao === 'aditivo';

  // Picker de signatário do aceite. Default operacional (a pessoa mais
  // próxima do uso do sistema), mas pode mudar pra financeiro ou pra um
  // contato manual quando quem aceita é outro (CEO, sócio, jurídico).
  const [signatarioFonte, setSignatarioFonte] = useState<SignatarioFonte>('operacional');
  const [signatarioManual, setSignatarioManual] = useState({
    nome: '',
    email: '',
    cpf: '',
    cargo: '',
    telefone: '',
  });

  // IP pra mostrar no preview (servidor captura também, é fonte de verdade)
  useEffect(() => {
    fetchClientIp().then((ip) => {
      if (ip) setClientIp(ip);
    });
  }, []);

  // Resolve o signatário escolhido pelo picker. Quando 'manual', usa o que o
  // usuário digitou; senão usa operacional ou financeiro das props.
  const signatarioResolvido = useMemo(() => {
    if (signatarioFonte === 'operacional') {
      return {
        nome: responsavelOperacional.nome,
        email: responsavelOperacional.email,
        cpf: responsavelOperacional.cpf || '',
        cargo: responsavelOperacional.cargo || 'Responsável Operacional',
        telefone: responsavelOperacional.celular,
      };
    }
    if (signatarioFonte === 'financeiro') {
      return {
        nome: responsavelFinanceiro.nome,
        email: responsavelFinanceiro.email,
        cpf: responsavelFinanceiro.cpf || '',
        cargo: responsavelFinanceiro.cargo || 'Responsável Financeiro',
        telefone: responsavelFinanceiro.celular,
      };
    }
    return {
      nome: signatarioManual.nome,
      email: signatarioManual.email,
      cpf: signatarioManual.cpf,
      cargo: signatarioManual.cargo || 'Representante',
      telefone: signatarioManual.telefone,
    };
  }, [signatarioFonte, signatarioManual, responsavelOperacional, responsavelFinanceiro]);

  const renderData: RenderTermoData = useMemo(
    () => ({
      token_aceite: token,
      data_aceite_extenso: formatDataAceiteExtenso(now),
      data_hora_aceite: formatDataHoraBR(now),
      ip_aceite: clientIp || '',
      user_agent_aceite:
        typeof navigator !== 'undefined' ? navigator.userAgent : '',

      razao_social_cliente: customerData.name || empresaHint?.razao_social || '',
      cnpj_cliente: customerData.cpfCnpj || empresaHint?.cnpj || '',
      endereco_cliente: [customerData.address, customerData.addressNumber, customerData.complement]
        .filter(Boolean)
        .join(', '),
      bairro_cliente: customerData.province,
      cidade_cliente: customerData.city,
      uf_cliente: uf || '',
      cep_cliente: customerData.postalCode,

      signatario_aceite_cliente: signatarioResolvido.nome,
      cargo_signatario_aceite: signatarioResolvido.cargo,
      cpf_signatario_aceite: signatarioResolvido.cpf || '—',
      email_signatario_aceite: signatarioResolvido.email,
      telefone_signatario_aceite: signatarioResolvido.telefone,

      nome_responsavel_operacional: responsavelOperacional.nome,
      cargo_responsavel_operacional: responsavelOperacional.cargo || 'Responsável Operacional',
      cpf_responsavel_operacional: responsavelOperacional.cpf || '—',
      email_responsavel_operacional: responsavelOperacional.email,
      telefone_responsavel_operacional: responsavelOperacional.celular,

      nome_contato_financeiro: responsavelFinanceiro.nome,
      email_contato_financeiro: responsavelFinanceiro.email,
      telefone_contato_financeiro: responsavelFinanceiro.celular,
      operacionais_adicionais: (operacionaisAdicionais || []).map(toContatoTermo),
      financeiros_adicionais: (financeirosAdicionais || []).map(toContatoTermo),

      nome_plano:
        dealTerms.nome_plano ||
        deriveNomePlano(
          dealTerms.fidelidade_meses || 12,
          dealTerms.volume_negociacoes_global || 500,
        ),
      prazo_minimo_meses: dealTerms.fidelidade_meses || 12,
      qtd_assistentes: dealTerms.num_assistentes || 1,
      qtd_whatsapp: dealTerms.qtd_whatsapp || dealTerms.num_assistentes || 1,
      qtd_usuarios_gestao: dealTerms.num_usuarios_gestao || 0,
      volume_negociacoes_por_assistente: dealTerms.volume_negociacoes_por_assistente || 0,
      volume_negociacoes_global: dealTerms.volume_negociacoes_global || 0,
      volume_global_periodo:
        dealTerms.volume_global_periodo === 'contrato' ? 'contrato' : 'mensal',
      volume_bonus_negociacoes: dealTerms.volume_bonus_negociacoes || 0,
      volume_bonus_periodo:
        dealTerms.volume_bonus_periodo === 'contrato' ? 'contrato' : 'mensal',
      valor_implantacao: formatCentavosBR(dealTerms.valor_implantacao_centavos || 0),
      valor_implantacao_centavos: dealTerms.valor_implantacao_centavos || 0,
      num_parcelas_implantacao: dealTerms.num_parcelas_implantacao || 1,
      entrada_implantacao_centavos: dealTerms.entrada_implantacao_centavos || 0,
      valor_parcela_implantacao_centavos:
        dealTerms.valor_parcela_implantacao_centavos ||
        dealTerms.valor_implantacao_centavos ||
        0,
      data_implantacao: formatDateBR(
        dealTerms.data_implantacao || dealTerms.data_inicio_prevista || '',
      ),
      valor_mensal: formatCentavosBR(dealTerms.valor_mensalidade_centavos || 0),
      valor_mensal_centavos: dealTerms.valor_mensalidade_centavos || 0,
      data_inicio_prevista: formatDateBR(dealTerms.data_inicio_prevista || ''),
      // v1.4.0 — mesmo mapeamento do backend (handlers.ts radarCreateAdhesion).
      data_primeira_cobranca: dealTerms.data_primeira_cobranca
        ? formatDateBR(dealTerms.data_primeira_cobranca)
        : '',
      forma_pagamento:
        dealTerms.forma_pagamento === 'pix' || dealTerms.forma_pagamento === 'cartao'
          ? dealTerms.forma_pagamento
          : 'boleto',
      cartao_incluir_implantacao: dealTerms.cartao_incluir_implantacao === true,
      forma_pagamento_implantacao:
        dealTerms.forma_pagamento_implantacao === 'boleto' ||
        dealTerms.forma_pagamento_implantacao === 'pix'
          ? dealTerms.forma_pagamento_implantacao
          : undefined,
      // v1.4.1 — pacote antecipado boleto/PIX (mesmo mapeamento do backend).
      pacote_antecipado: dealTerms.pacote_antecipado === true,
      pacote_parcelas: dealTerms.pacote_parcelas || 0,
      pacote_datas: Array.isArray(dealTerms.pacote_datas)
        ? dealTerms.pacote_datas.map((d: string) => formatDateBR(d))
        : [],
      pacote_incluir_implantacao: dealTerms.pacote_incluir_implantacao !== false,
      dia_vencimento_mensal: dealTerms.dia_vencimento || 15,
      modelo_cobranca: dealTerms.modelo_cobranca === 'pos_pago' ? 'pos_pago' : 'pre_pago',
      pro_rata_primeira_mensalidade: dealTerms.pro_rata_primeira_mensalidade === true,
      condicoes_especiais: dealTerms.condicoes_especiais || undefined,
    }),
    [
      token,
      now,
      clientIp,
      customerData,
      empresaHint,
      uf,
      signatarioResolvido,
      responsavelOperacional,
      responsavelFinanceiro,
      operacionaisAdicionais,
      financeirosAdicionais,
      dealTerms,
    ],
  );

  // Modo aditivo: dados do Termo de Recurso Adicional. Reaproveita empresa,
  // contatos e metadados do aceite já montados em `renderData`; o recurso vem
  // das chaves do deal_terms gravadas pelo Radar (mesmo mapeamento do backend).
  const renderDataAditivo: RenderTermoAditivoData = useMemo(
    () => ({
      token_aceite: renderData.token_aceite,
      data_aceite_extenso: renderData.data_aceite_extenso,
      data_hora_aceite: renderData.data_hora_aceite,
      ip_aceite: renderData.ip_aceite,
      user_agent_aceite: renderData.user_agent_aceite,

      parent_adhesion_id: dealTerms.parent_adhesion_id || '',
      parent_document_hash: dealTerms.parent_document_hash || '',
      parent_accepted_at: dealTerms.parent_accepted_at || '',

      razao_social_cliente: renderData.razao_social_cliente,
      cnpj_cliente: renderData.cnpj_cliente,
      endereco_cliente: renderData.endereco_cliente,
      bairro_cliente: renderData.bairro_cliente,
      cidade_cliente: renderData.cidade_cliente,
      uf_cliente: renderData.uf_cliente,
      cep_cliente: renderData.cep_cliente,

      signatario_aceite_cliente: renderData.signatario_aceite_cliente,
      cargo_signatario_aceite: renderData.cargo_signatario_aceite,
      cpf_signatario_aceite: renderData.cpf_signatario_aceite,
      email_signatario_aceite: renderData.email_signatario_aceite,
      telefone_signatario_aceite: renderData.telefone_signatario_aceite,
      nome_responsavel_operacional: renderData.nome_responsavel_operacional,
      cargo_responsavel_operacional: renderData.cargo_responsavel_operacional,
      email_responsavel_operacional: renderData.email_responsavel_operacional,
      telefone_responsavel_operacional: renderData.telefone_responsavel_operacional,
      nome_contato_financeiro: renderData.nome_contato_financeiro,
      email_contato_financeiro: renderData.email_contato_financeiro,
      telefone_contato_financeiro: renderData.telefone_contato_financeiro,
      operacionais_adicionais: renderData.operacionais_adicionais,
      financeiros_adicionais: renderData.financeiros_adicionais,

      nome_recurso: dealTerms.produto_nome || '',
      descricao_recurso: dealTerms.produto_descricao || '',
      valor_mensal: formatCentavosBR(dealTerms.valor_mensalidade_centavos || 0),
      valor_implantacao_centavos: dealTerms.valor_implantacao_centavos || 0,
      data_ativacao: formatDateBR(dealTerms.data_inicio_prevista || ''),
      dia_vencimento_mensal: dealTerms.dia_vencimento || 15,
      pro_rata_primeira_mensalidade: dealTerms.pro_rata_primeira_mensalidade === true,
      conta_separada: dealTerms.conta_separada === true,
      condicoes_especiais: dealTerms.condicoes_especiais || undefined,
    }),
    [renderData, dealTerms],
  );

  const termoMarkdown = useMemo(
    () => (isAditivo ? renderTermoAditivoPreview(renderDataAditivo) : renderTermoPreview(renderData)),
    [isAditivo, renderDataAditivo, renderData],
  );

  // Rótulos que mudam entre termo principal e aditivo.
  const nomeTermo = isAditivo ? 'Termo de Recurso Adicional' : 'Termo de Adesão e Uso';
  const versaoTermo = isAditivo ? TERMO_ADITIVO_VERSION : '1.0';

  // Cópia do termo pra impressão ANTES do aceite: o preview traz o bloco
  // "Registros do aceite" já preenchido com token, data/hora atual, IP e
  // User-Agent, e fecha com "Adesão registrada eletronicamente em…" — num PDF
  // mandado pro cliente revisar isso parece assinado e expõe o token. Corta
  // dali pra frente e explica que o registro nasce na assinatura.
  const termoMarkdownImpressao = useMemo(() => {
    const marcador = '**Registros do aceite';
    const idx = termoMarkdown.indexOf(marcador);
    const corpo = idx >= 0 ? termoMarkdown.slice(0, idx).trimEnd() : termoMarkdown;
    return (
      corpo +
      '\n\n---\n\n*Minuta para revisão, sem validade até o aceite eletrônico. ' +
      'Os registros do aceite (token, data e hora, endereço IP e hash do documento) ' +
      'são preenchidos automaticamente no momento da assinatura.*\n'
    );
  }, [termoMarkdown]);

  // "Baixar PDF" antes da assinatura (#melhorias-radar, Simone 19/08): a
  // vendedora precisa mandar a minuta pra aprovação interna do cliente. Mesma
  // mecânica do viewer /termo/:id (window.print + @media print), mas aqui a
  // página tem formulários e um ScrollArea de 400px em volta do termo — então
  // a impressão sai de uma cópia limpa do termo montada num portal fora do
  // #root, e `body.print-termo-only` esconde o resto (ver index.css).
  const [printing, setPrinting] = useState(false);
  useEffect(() => {
    if (!printing) return;
    document.body.classList.add('print-termo-only');
    let finished = false;
    const done = () => {
      if (finished) return;
      finished = true;
      document.body.classList.remove('print-termo-only');
      setPrinting(false);
    };
    window.addEventListener('afterprint', done);
    // Deixa o React pintar o portal antes de abrir o diálogo. window.print()
    // bloqueia até o diálogo fechar na maioria dos navegadores; onde não
    // bloqueia (Safari), o afterprint encerra.
    const timer = window.setTimeout(() => {
      window.print();
      done();
    }, 60);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('afterprint', done);
      document.body.classList.remove('print-termo-only');
    };
  }, [printing]);

  async function handleAcceptar() {
    if (!accepted) {
      toast.error('Marque "Li e aceito" antes de continuar.');
      return;
    }

    if (!uf) {
      toast.error(
        'UF não capturada do CNPJ. Volte à Etapa 1 e confirme a empresa antes de aceitar.',
      );
      return;
    }

    if (signatarioFonte === 'manual' && (!signatarioManual.nome || !signatarioManual.email)) {
      toast.error('Informe nome e e-mail do signatário antes de aceitar.');
      return;
    }

    const signatarioContato = {
      nome: signatarioResolvido.nome,
      cargo: signatarioResolvido.cargo,
      cpf: signatarioResolvido.cpf || undefined,
      email: signatarioResolvido.email,
      telefone: signatarioResolvido.telefone,
    };

    const payload: CreateAdhesionPayload = {
      token,
      empresa: {
        endereco: [customerData.address, customerData.addressNumber, customerData.complement]
          .filter(Boolean)
          .join(', '),
        bairro: customerData.province,
        cidade: customerData.city,
        uf,
        cep: customerData.postalCode,
      },
      signatario_aceite: { ...signatarioContato, fonte: signatarioFonte },
      // Echo compat: backend antigo lê `representante` como signatário até
      // que a leitura de `signatario_aceite` seja deployada (ver plano).
      representante: signatarioContato,
      operacional: {
        nome: responsavelOperacional.nome,
        cargo: responsavelOperacional.cargo || 'Responsável Operacional',
        cpf: responsavelOperacional.cpf || undefined,
        email: responsavelOperacional.email,
        telefone: responsavelOperacional.celular,
      },
      financeiro: {
        nome: responsavelFinanceiro.nome,
        cargo: responsavelFinanceiro.cargo || 'Responsável Financeiro',
        cpf: responsavelFinanceiro.cpf || undefined,
        email: responsavelFinanceiro.email,
        telefone: responsavelFinanceiro.celular,
      },
      operacionais_adicionais: (operacionaisAdicionais || []).map(toContatoPayload),
      financeiros_adicionais: (financeirosAdicionais || []).map(toContatoPayload),
      // Contrato formal é opcional e oferecido DEPOIS, como CTA na tela de sucesso
      // (/cadastro-sucesso → /contrato-formal/:adhesionId). No aceite do termo é
      // sempre false. O backend trata como `=== true`, então omitir = false.
      quer_contrato_formal: false,
    };

    setSubmitting(true);
    try {
      const result = await createAdhesion(payload);
      toast.success(isAditivo ? 'Termo aceito! Recurso adicional contratado.' : 'Termo aceito! Bem-vindo à DGenny.');
      onAccepted(result.adhesionId, {
        companyId: result.company?.id || '',
      });
    } catch (err: any) {
      const msg = err?.message || 'Erro ao registrar aceite. Tente novamente.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-brand-azul mb-2">{nomeTermo}</h2>
        <p className="text-sm text-muted-foreground">
          {isAditivo
            ? 'Confirme os dados, leia o termo e aceite eletronicamente pra ativar o recurso adicional.'
            : 'Confirme os dados, leia o termo e aceite eletronicamente pra ativar a contratação.'}
        </p>
      </div>

      {/* Resumo da proposta (Seção 3 destacada) — no aditivo, resumo do recurso */}
      <Card className="p-4 bg-brand-azul/5 border-brand-azul/20">
        <h3 className="font-semibold text-brand-azul mb-3 flex items-center gap-2">
          <FileText className="w-4 h-4" />
          {isAditivo ? 'Resumo do recurso adicional' : 'Resumo da proposta aprovada'}
        </h3>
        {isAditivo ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <ResumoItem label="Recurso" value={renderDataAditivo.nome_recurso} highlight />
            <ResumoItem label="Valor mensal" value={`R$ ${renderDataAditivo.valor_mensal}`} highlight />
            <ResumoItem label="CNPJ contratante" value={renderDataAditivo.cnpj_cliente} />
            <ResumoItem label="Conta separada" value={renderDataAditivo.conta_separada ? 'Sim' : 'Não'} />
            {renderDataAditivo.valor_implantacao_centavos > 0 && (
              <ResumoItem
                label="Implantação"
                value={`R$ ${formatCentavosBR(renderDataAditivo.valor_implantacao_centavos)}`}
              />
            )}
            <ResumoItem label="Ativação prevista" value={renderDataAditivo.data_ativacao} />
            <ResumoItem label="Vencimento" value={`dia ${renderDataAditivo.dia_vencimento_mensal}`} />
            {renderDataAditivo.pro_rata_primeira_mensalidade && (
              <ResumoItem label="Pro rata 1ª mensalidade" value="Sim" />
            )}
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <ResumoItem label="Plano" value={renderData.nome_plano} />
          <ResumoItem label="Prazo mínimo" value={`${renderData.prazo_minimo_meses} meses`} />
          <ResumoItem label="Assistentes de IA" value={String(renderData.qtd_assistentes)} />
          <ResumoItem label="Contas de WhatsApp" value={String(renderData.qtd_whatsapp)} />
          <ResumoItem label="Usuários de gestão" value={String(renderData.qtd_usuarios_gestao)} />
          <ResumoItem
            label="Volume por assistente/mês"
            value={String(renderData.volume_negociacoes_por_assistente)}
          />
          <ResumoItem
            label={
              renderData.volume_global_periodo === 'contrato'
                ? 'Volume global no contrato'
                : 'Volume global/mês'
            }
            value={String(renderData.volume_negociacoes_global)}
          />
          {renderData.volume_bonus_negociacoes > 0 && (
            <ResumoItem
              label={
                renderData.volume_bonus_periodo === 'contrato'
                  ? 'Bônus de negociações (contrato)'
                  : 'Bônus de negociações/mês'
              }
              value={String(renderData.volume_bonus_negociacoes)}
            />
          )}
          <ResumoItem
            label="Implantação"
            value={
              renderData.num_parcelas_implantacao > 1
                ? `R$ ${renderData.valor_implantacao} (${renderData.num_parcelas_implantacao}x)`
                : `R$ ${renderData.valor_implantacao}`
            }
            highlight
          />
          <ResumoItem
            label="Mensalidade"
            value={`R$ ${renderData.valor_mensal}`}
            highlight
          />
          <ResumoItem
            label="Cobrança"
            value={renderData.modelo_cobranca === 'pos_pago' ? 'Pós-pago' : 'Pré-pago'}
          />
          <ResumoItem label="Início previsto" value={renderData.data_inicio_prevista} />
          <ResumoItem label="Vencimento" value={`dia ${renderData.dia_vencimento_mensal}`} />
          {renderData.pro_rata_primeira_mensalidade && (
            <ResumoItem label="Pro rata 1ª mensalidade" value="Sim" />
          )}
        </div>
        )}
      </Card>

      {/* Resumo dos responsáveis */}
      <Card className="p-4">
        <h3 className="font-semibold text-brand-azul mb-3">Contatos da empresa</h3>
        <div className="space-y-3 text-sm">
          <ContatoBloco
            titulo="Responsável operacional"
            nome={responsavelOperacional.nome}
            email={responsavelOperacional.email}
            telefone={responsavelOperacional.celular}
            cargo={responsavelOperacional.cargo}
          />
          <Separator />
          <ContatoBloco
            titulo="Responsável financeiro"
            nome={responsavelFinanceiro.nome}
            email={responsavelFinanceiro.email}
            telefone={responsavelFinanceiro.celular}
            cargo={responsavelFinanceiro.cargo}
          />
        </div>
        <Button variant="ghost" size="sm" onClick={onBack} className="mt-3">
          ← Editar dados anteriores
        </Button>
      </Card>

      {/* Picker de signatário do aceite */}
      <Card className="p-4 border-brand-azul/20">
        <h3 className="font-semibold text-brand-azul mb-1">Quem está aceitando este termo?</h3>
        <p className="text-xs text-muted-foreground mb-3">
          Indique a pessoa que está clicando no aceite agora. Esse nome será registrado no
          carimbo de tempo do termo.
        </p>
        <div className="space-y-2 text-sm">
          <SignatarioRadio
            checked={signatarioFonte === 'operacional'}
            onChange={() => setSignatarioFonte('operacional')}
            label="Responsável Operacional"
            sublabel={
              responsavelOperacional.nome
                ? `${responsavelOperacional.nome} · ${responsavelOperacional.email}`
                : 'preencha na Etapa 3'
            }
            disabled={!responsavelOperacional.nome || !responsavelOperacional.email}
          />
          <SignatarioRadio
            checked={signatarioFonte === 'financeiro'}
            onChange={() => setSignatarioFonte('financeiro')}
            label="Responsável Financeiro"
            sublabel={
              responsavelFinanceiro.nome
                ? `${responsavelFinanceiro.nome} · ${responsavelFinanceiro.email}`
                : 'preencha na Etapa 3'
            }
            disabled={!responsavelFinanceiro.nome || !responsavelFinanceiro.email}
          />
          <SignatarioRadio
            checked={signatarioFonte === 'manual'}
            onChange={() => setSignatarioFonte('manual')}
            label="Outra pessoa"
            sublabel="Informo os dados abaixo"
          />
        </div>
        {signatarioFonte === 'manual' && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-brand-azul/5 rounded-md">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Nome completo *</label>
              <input
                type="text"
                value={signatarioManual.nome}
                onChange={(e) => setSignatarioManual((p) => ({ ...p, nome: e.target.value }))}
                placeholder="Nome de quem está aceitando"
                className="w-full text-sm px-3 py-1.5 border border-input rounded-md bg-background"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">E-mail *</label>
              <input
                type="email"
                value={signatarioManual.email}
                onChange={(e) => setSignatarioManual((p) => ({ ...p, email: e.target.value }))}
                placeholder="email@empresa.com"
                className="w-full text-sm px-3 py-1.5 border border-input rounded-md bg-background"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Cargo</label>
              <input
                type="text"
                value={signatarioManual.cargo}
                onChange={(e) => setSignatarioManual((p) => ({ ...p, cargo: e.target.value }))}
                placeholder="ex: CEO, Sócio"
                className="w-full text-sm px-3 py-1.5 border border-input rounded-md bg-background"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">CPF</label>
              <input
                type="text"
                value={signatarioManual.cpf}
                onChange={(e) => setSignatarioManual((p) => ({ ...p, cpf: e.target.value }))}
                placeholder="000.000.000-00"
                className="w-full text-sm px-3 py-1.5 border border-input rounded-md bg-background"
              />
            </div>
            <div className="space-y-1 md:col-span-2">
              <label className="text-xs text-muted-foreground">Telefone</label>
              <input
                type="text"
                value={signatarioManual.telefone}
                onChange={(e) => setSignatarioManual((p) => ({ ...p, telefone: e.target.value }))}
                placeholder="(00) 00000-0000"
                className="w-full text-sm px-3 py-1.5 border border-input rounded-md bg-background"
              />
            </div>
          </div>
        )}
      </Card>

      {/* Termo renderizado */}
      <Card className="p-0 overflow-hidden">
        <div className="bg-muted px-4 py-2 border-b text-sm font-semibold flex items-center justify-between">
          <span className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            {nomeTermo} (leia antes de aceitar)
          </span>
          <span className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground font-normal">
              Versão {versaoTermo}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs font-normal"
              onClick={() => setPrinting(true)}
              disabled={printing || submitting}
              title="Gera um PDF deste termo, sem validade até o aceite eletrônico"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" />
              Baixar PDF
            </Button>
          </span>
        </div>
        <ScrollArea className="h-[400px]">
          <div className="px-6 py-5 text-sm leading-relaxed text-foreground/90">
            <TermoRendered markdown={termoMarkdown} />
          </div>
        </ScrollArea>
      </Card>

      {/* Aceite */}
      <Card className="p-4 border-brand-roxo/30">
        <div className="flex items-start gap-3">
          <Checkbox
            id="aceite"
            checked={accepted}
            onCheckedChange={(v) => setAccepted(v === true)}
            disabled={submitting}
            className="mt-1"
          />
          <label htmlFor="aceite" className="text-sm cursor-pointer flex-1">
            <strong>Li, compreendi e aceito</strong> o {nomeTermo} acima em nome
            da <strong>{renderData.razao_social_cliente}</strong>.
          </label>
        </div>
      </Card>

      <div className="flex justify-between gap-3">
        <Button variant="outline" onClick={onBack} disabled={submitting}>
          Voltar
        </Button>
        <Button
          onClick={handleAcceptar}
          disabled={!accepted || submitting}
          className="bg-brand-roxo hover:bg-brand-roxo/90"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Registrando aceite...
            </>
          ) : (
            <>
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Aceitar e ativar
            </>
          )}
        </Button>
      </div>

      {printing &&
        createPortal(
          <div className="termo-print-area hidden print:block text-sm leading-relaxed text-foreground">
            <div className="text-xs text-muted-foreground mb-6">
              {nomeTermo} · minuta para revisão de {renderData.razao_social_cliente} · gerada em{' '}
              {new Date().toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })} · sem
              validade até o aceite eletrônico
            </div>
            <article>
              <TermoRendered markdown={termoMarkdownImpressao} />
            </article>
          </div>,
          document.body,
        )}
    </div>
  );
}

function ResumoItem({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}:</span>
      <span className={highlight ? 'font-semibold text-brand-roxo' : 'font-medium'}>
        {value}
      </span>
    </div>
  );
}

function SignatarioRadio({
  checked,
  onChange,
  label,
  sublabel,
  disabled,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
  sublabel: string;
  disabled?: boolean;
}) {
  return (
    <label
      className={`flex items-start gap-2 p-2 rounded-md border ${
        checked ? 'border-brand-roxo bg-brand-roxo/5' : 'border-transparent hover:bg-muted/40'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <input
        type="radio"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="mt-1"
      />
      <div className="flex-1">
        <div className="font-medium text-foreground">{label}</div>
        <div className="text-xs text-muted-foreground">{sublabel}</div>
      </div>
    </label>
  );
}

function ContatoBloco({
  titulo,
  nome,
  email,
  telefone,
  cargo,
}: {
  titulo: string;
  nome: string;
  email: string;
  telefone?: string;
  cargo?: string | null;
}) {
  return (
    <div>
      <div className="text-xs font-semibold text-muted-foreground uppercase mb-1">
        {titulo}
      </div>
      <div className="font-medium">{nome}</div>
      <div className="text-xs text-muted-foreground space-x-2">
        {cargo && <span>{cargo}</span>}
        {cargo && <span>·</span>}
        <span>{email}</span>
        {telefone && (
          <>
            <span>·</span>
            <span>{telefone}</span>
          </>
        )}
      </div>
    </div>
  );
}
